/* eslint-disable react/no-array-index-key */
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { ResizeObserver } from '@juggle/resize-observer';
import { largeTabletMax } from '@propertypal/shared/src/constants/mediaQueries';
import Link from 'next/link';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import useMeasure from 'react-use-measure';
import { DefaultTheme } from 'styled-components';
import ProductSlideshow, { StaticImage } from '../animation/ProductSlideshow';
import Button, { Props } from '../buttons/Button';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import useMediaQuery from '../media-query/useMediaQuery';
import { SmallHeading, Text } from '../typography';
import {
  CheckIcon,
  Container,
  Content,
  MockupContainer,
  NavIcon,
  ProductBadge,
  TabContent,
  TabText,
  TabTextContainer,
  TabsContainer,
  Wrapper,
  ButtonContainer,
  SmalPrint,
  TabsWrapper,
  StaticContent,
} from './ProductScreenTabs.style';

/* istanbul ignore next */
const TIMEOUT = process?.env?.JEST_WORKER_ID ? 100 : 5000; // 5 seconds

export interface ProductScreenTab {
  title: string;
  text: string | React.ReactNode;
  mockup?: string;
  maxW?: number;
  hideArrow?: boolean;
}

export interface ProductScreenBadge {
  title: string;
  text?: string;
  color: keyof DefaultTheme;
  titleFontSize?: number;
  titleFontSizeMob?: number;
  titleLineHeight?: string;
  x?: number;
  xLap?: number;
  xMob?: number;
}

export interface ProductScreenButton {
  text: string;
  href?: string;
  onClick?: (e: React.MouseEvent<Element, MouseEvent>) => void;
  containerStyle?: Props['containerStyle'];
}

interface ProductScreenTabsProps {
  tabs?: ProductScreenTab[];
  badge?: ProductScreenBadge;
  button?: ProductScreenButton;
  aspectRatio?: string;
  mockupWidth?: number;
  negMargin?: number;
  reverse?: boolean;
  maxW?: number;
  breakpoint?: number;
  smallPrint?: string | React.ReactNode;
  smallPrintMt?: number;
  pb?: number;
  tabsMt?: number;
  tabPadding?: number;
  staticTabs?: boolean;
  content?: React.ReactNode;
  mockup?: {
    src: string;
    alt: string;
  };
  buttonXPadding?: number;
  buttonMt?: number;
  showCheck?: boolean;
  mobPadding?: boolean;
}

const ProductScreenTabs: React.FC<ProductScreenTabsProps> = ({
  tabs,
  badge,
  button,
  aspectRatio,
  mockupWidth,
  negMargin,
  reverse,
  maxW,
  breakpoint,
  smallPrint,
  smallPrintMt,
  pb,
  tabsMt,
  tabPadding,
  staticTabs,
  content,
  mockup,
  buttonXPadding,
  buttonMt,
  showCheck,
  mobPadding,
}) => {
  // memoise whether tabs should loop, they should not loop if tabs are static, or using jsx content
  const isStatic = useMemo(() => !!(staticTabs || content), [staticTabs, content]);
  // set key to the first tab item key, unless static
  const [activeKey, setActiveKey] = useState<number | null>(isStatic ? (tabs?.length === 1 && content ? 0 : null) : 0);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // memoise keys to prevent unnecessary re-renders
  const keys = useMemo(() => tabs?.map((_, i) => i), [tabs]);

  const isMobile = useMediaQuery({ query: `(${breakpoint ? `max-width: ${breakpoint}px` : largeTabletMax})` });

  const handleClick = (key: number) => {
    if (!isMobile && !isStatic) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setActiveKey(key);
    }
  };

  useEffect(() => {
    // don't add active tabs if static
    if (!isStatic && keys) {
      // set active key to the next key, when reaching last key, the next key is the first key
      const cycleActiveState = () => setActiveKey(keys[(keys.indexOf(activeKey!) + 1) % keys.length]);
      // restart timer
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      // set interval
      timeoutRef.current = setInterval(cycleActiveState, TIMEOUT);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current); // Cleanup on unmount
    };
  }, [activeKey, keys, isStatic]);

  // use to get height of active tab content when position absolute on mobile, to use as height of relative parent
  const [heightRef, { height }] = useMeasure({ polyfill: ResizeObserver });

  return (
    <Container
      pb={pb}
      breakpoint={breakpoint}
      mobPadding={!!badge || mobPadding}
      data-testid="product-content-container"
    >
      <Wrapper maxW={maxW} data-testid="product-content-wrapper">
        <Content
          reverse={reverse}
          negMargin={negMargin}
          badgeXPos={badge?.x}
          badgeXPosLap={badge?.xLap}
          badgeXPosMob={badge?.xMob}
          breakpoint={breakpoint}
          mockupWidth={mockupWidth}
          tabHeight={height}
          tabsMt={tabsMt}
          tabPadding={tabPadding}
          staticTabs={staticTabs}
          staticContent={!!content || false}
          buttonXPadding={buttonXPadding}
          buttonMt={buttonMt}
          data-testid="product-features-content"
        >
          <MockupContainer data-testid="product-mockup-container">
            {badge && (
              <ProductBadge
                color={badge.color}
                data-testid="product-badge"
                titleFontSize={badge.titleFontSize}
                titleFontSizeMob={badge.titleFontSizeMob}
                titleLineHeight={badge.titleLineHeight}
              >
                <span className="badge-title">{badge.title}</span>
                {badge.text && <span className="badge-text">{badge.text}</span>}
              </ProductBadge>
            )}
            {mockup ? (
              <StaticImage aspectRatio={aspectRatio || '821 / 683'} src={mockup.src} alt={mockup.alt} />
            ) : (
              tabs && (
                <ProductSlideshow
                  images={tabs.map((t) => t.mockup || '')}
                  alts={tabs.map((t) => t.title)}
                  aspectRatio={aspectRatio || '821 / 683'}
                  activeKey={activeKey || 0}
                />
              )
            )}
          </MockupContainer>
          <TabsContainer>
            <TabsWrapper>
              {tabs &&
                keys &&
                tabs?.map((item, i) => (
                  <TabContent
                    key={item.title}
                    active={activeKey === i}
                    onClick={() => handleClick(i)}
                    data-active={activeKey === i}
                    data-testid="product-tab"
                    breakpoint={breakpoint}
                    staticTabs={staticTabs}
                  >
                    <TabTextContainer ref={activeKey === i ? heightRef : undefined}>
                      {(!staticTabs || showCheck) && <CheckIcon testID="product-check" icon={faCheck} />}
                      <TabText>
                        <SmallHeading fontWeight="800" fontSize={20}>
                          {item.title}
                        </SmallHeading>
                        <Text maxWidth={item.maxW}>{item.text}</Text>
                      </TabText>
                      {!(item.hideArrow || staticTabs) && (
                        <NavIcon
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setActiveKey(keys[(i + 1) % keys.length]);
                          }}
                          data-testid="product-arrow"
                        >
                          <FontAwesomeIcon icon={faChevronRight} size={22} />
                        </NavIcon>
                      )}
                    </TabTextContainer>
                  </TabContent>
                ))}
              {content && <StaticContent>{content}</StaticContent>}
            </TabsWrapper>
            {button && (
              <ButtonContainer>
                {button.href ? (
                  <Link href={button.href} prefetch={false}>
                    <Button containerStyle={button.containerStyle} testID="product-button">
                      {button.text}
                    </Button>
                  </Link>
                ) : (
                  <Button containerStyle={button.containerStyle} onClick={button.onClick} testID="product-button">
                    {button.text}
                  </Button>
                )}
              </ButtonContainer>
            )}
          </TabsContainer>
        </Content>
        {smallPrint && (
          <SmalPrint mt={smallPrintMt} data-testid="product-small-print">
            {smallPrint}
          </SmalPrint>
        )}
      </Wrapper>
    </Container>
  );
};

export default ProductScreenTabs;
